import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
  Card,
  CircularProgress,
  Grid,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme
} from '@mui/material';
import { I_Installation } from 'src/interfaces/InstallationTypes';
import { FormattedMessage } from 'react-intl';
import { useLocation, useNavigate } from 'react-router-dom';
import routes from '../../../Resources/routes.json';
import CancelIcon from '@mui/icons-material/Cancel';
import BuildIcon from '@mui/icons-material/Build';
import { getDeviceTypeName } from '../Information/installationSetupUtils';
import { UserContext } from 'src/contexts/userContext';
import { UserType } from 'src/interfaces/UserTypes';
import axiosConfig from 'src/Resources/axiosConfig';
import {
  CHECKLIST_ENABLED_PRODUCTS,
  ChecklistSummary
} from 'src/interfaces/ChecklistTypes';
import SetupProgress from '../Checklist/SetupProgress';

interface FlatInstallationViewProps {
  installations: I_Installation[];
  product?: number;
}

const FlatInstallationView = (props: FlatInstallationViewProps) => {
  const navigate = useNavigate();
  const [selectedInstallation, setSelectedInstallation] = useState<number>(-1);
  const currentLocation = useLocation();
  const baseRoute = props.product === 5 ? routes.sodistorepro_installations : routes.sodiohome_installations;

  const { currentUser } = useContext(UserContext);
  const showChecklistColumn =
    currentUser?.userType === UserType.admin &&
    CHECKLIST_ENABLED_PRODUCTS.has(props.product ?? -1);
  const isListViewPath =
    currentLocation.pathname === baseRoute + 'list' ||
    currentLocation.pathname === baseRoute + routes.list;
  const [progressMap, setProgressMap] = useState<Map<number, ChecklistSummary>>(
    new Map()
  );

  useEffect(() => {
    if (!showChecklistColumn || !isListViewPath) return;
    axiosConfig
      .get('/GetChecklistSummary')
      .then((res) => {
        if (!Array.isArray(res.data)) return;
        const map = new Map<number, ChecklistSummary>();
        res.data.forEach((s: ChecklistSummary) => {
          map.set(s.installationId, s);
        });
        setProgressMap(map);
      })
      .catch(() => setProgressMap(new Map()));
  }, [showChecklistColumn, isListViewPath]);

  const sortedInstallations = useMemo(() => {
    return [...props.installations].sort((a, b) => {
      // Data-collection-disabled installations sink below everything (even offline).
      const aDisabled = a.dataCollectionEnabled === false;
      const bDisabled = b.dataCollectionEnabled === false;
      if (aDisabled !== bDisabled) return aDisabled ? 1 : -1;

      // Then sort by status (alarms first)
      const a_status = a.status;
      const b_status = b.status;

      if (a_status > b_status) return -1;
      if (a_status < b_status) return 1;
      return 0;
    });
  }, [props.installations]);

  const handleSelectOneInstallation = (installationID: number): void => {
    if (selectedInstallation != installationID) {
      setSelectedInstallation(installationID);
      setSelectedInstallation(-1);

      const target = props.installations.find((i) => i.id === installationID);
      const landingTab =
        target?.dataCollectionEnabled === false ? routes.information : routes.live;

      navigate(
        baseRoute +
          routes.list +
          routes.installation +
          `${installationID}` +
          '/' +
          landingTab,
        {
          replace: true
        }
      );
    } else {
      setSelectedInstallation(-1);
    }
  };

  const theme = useTheme();

  const HoverableTableRow = styled(TableRow)(({ theme }) => ({
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.action.hover // Or any other color
    }
  }));

  const isListView = isListViewPath;

  return (
    <Grid container spacing={1} sx={{ marginTop: 0.1 }}>
      <Grid
        item
        sx={{
          display: isListView ? 'block' : 'none'
        }}
      >
        <Card>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <FormattedMessage id="name" defaultMessage="Name" />
                  </TableCell>
                  <TableCell>
                    <FormattedMessage id="installationSN" defaultMessage="Installation SN" />
                  </TableCell>
                  <TableCell>
                    <FormattedMessage id="DeviceType" defaultMessage="Device Type" />
                  </TableCell>
                  <TableCell>
                    <FormattedMessage id="canton" defaultMessage="Canton" />
                  </TableCell>
                  <TableCell>
                    <FormattedMessage id="country" defaultMessage="Country" />
                  </TableCell>
                  <TableCell>
                    <FormattedMessage id="status" defaultMessage="Status" />
                  </TableCell>
                  {showChecklistColumn && (
                    <TableCell>
                      <FormattedMessage
                        id="setupProgress"
                        defaultMessage="Setup Progress"
                      />
                    </TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedInstallations
                  // .filter(
                  //   (installation) =>
                  //     installation.status === -1 &&
                  //     installation.testingMode == false
                  // )
                  .map((installation) => {
                    const isInstallationSelected =
                      installation.id === selectedInstallation;

                    const status = installation.status;

                    return (
                      <HoverableTableRow
                        key={installation.id}
                        onClick={() =>
                          handleSelectOneInstallation(installation.id)
                        }
                      >
                        <TableCell>
                          <Typography
                            variant="body2"
                            fontWeight="bold"
                            color="text.primary"
                            gutterBottom
                            noWrap
                            sx={{ marginTop: '10px', fontSize: 'small' }}
                          >
                            {installation.name}
                          </Typography>
                        </TableCell>

                        <TableCell>
                          <Typography
                            variant="body2"
                            fontWeight="bold"
                            color="text.primary"
                            gutterBottom
                            noWrap
                            sx={{ marginTop: '10px', fontSize: 'small' }}
                          >
                            {installation.serialNumber}
                          </Typography>
                        </TableCell>

                        <TableCell>
                          <Typography
                            variant="body2"
                            fontWeight="bold"
                            color="text.primary"
                            gutterBottom
                            noWrap
                            sx={{ marginTop: '10px', fontSize: 'small' }}
                          >
                            {getDeviceTypeName(installation.device)}
                          </Typography>
                        </TableCell>

                        <TableCell>
                          <Typography
                            variant="body2"
                            fontWeight="bold"
                            color="text.primary"
                            gutterBottom
                            noWrap
                            sx={{ marginTop: '10px', fontSize: 'small' }}
                          >
                            {installation.canton || ''}
                          </Typography>
                        </TableCell>

                        <TableCell>
                          <Typography
                            variant="body2"
                            fontWeight="bold"
                            color="text.primary"
                            gutterBottom
                            noWrap
                            sx={{ marginTop: '10px', fontSize: 'small' }}
                          >
                            {installation.country}
                          </Typography>
                        </TableCell>

                        <TableCell>
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              marginLeft: '15px'
                            }}
                          >
                            {installation.dataCollectionEnabled === false ? (
                              <div
                                style={{
                                  width: '20px',
                                  height: '20px',
                                  marginLeft: '2px',
                                  borderRadius: '50%',
                                  backgroundColor: 'grey'
                                }}
                              />
                            ) : (
                              <>
                                {status === -1 ? (
                                  <CancelIcon
                                    style={{
                                      width: '23px',
                                      height: '23px',
                                      color: 'red',
                                      borderRadius: '50%'
                                    }}
                                  />
                                ) : (
                                  ''
                                )}

                                {status === -2 ? (
                                  <CircularProgress
                                    size={20}
                                    sx={{
                                      color: '#f7b34d'
                                    }}
                                  />
                                ) : (
                                  ''
                                )}

                                <div
                                  style={{
                                    width: '20px',
                                    height: '20px',
                                    marginLeft: '2px',
                                    borderRadius: '50%',
                                    backgroundColor:
                                      status === 2
                                        ? 'red'
                                        : status === 1
                                        ? 'orange'
                                        : status === -1 || status === -2
                                        ? 'transparent'
                                        : 'green'
                                  }}
                                />
                              </>
                            )}
                            {installation.testingMode && (
                              <BuildIcon
                                style={{
                                  width: '23px',
                                  height: '23px',
                                  color: 'purple',
                                  borderRadius: '50%',
                                  position: 'relative',
                                  zIndex: 1,
                                  marginLeft: status != -1 ? '25px' : '0px'
                                }}
                              />
                            )}
                          </div>
                        </TableCell>
                        {showChecklistColumn && (
                          <TableCell>
                            {(() => {
                              const summary = progressMap.get(installation.id);
                              return (
                                <SetupProgress
                                  done={summary?.done ?? 0}
                                  total={summary?.total ?? 16}
                                />
                              );
                            })()}
                          </TableCell>
                        )}
                      </HoverableTableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      </Grid>
    </Grid>
  );
};

export default FlatInstallationView;
